import jsPDF from 'jspdf'
import 'jspdf-autotable'

export const convertImageToBase64 = (imageUrl) => {
	return new Promise((resolve, reject) => {
		const img = new Image()
		img.crossOrigin = "Anonymous"
		img.src = imageUrl
		img.onload = () => {
			const canvas = document.createElement("canvas")
			canvas.width = img.width
			canvas.height = img.height
			const ctx = canvas.getContext("2d")
			ctx.drawImage(img, 0, 0)
			const dataURL = canvas.toDataURL("image/png")
			resolve(dataURL)
		}
		img.onerror = reject
	})
}

export const generateApprovedQuotationPDF = async (bidOrder, workOrder, selectedItems = [], quotationObservations = '', userEmail = '') => {
	const doc = new jsPDF()

	// Función auxiliar para dibujar el header en una página
	const drawHeader = async (pageDoc) => {
		// Asegurar que el color de dibujo sea negro y el ancho de línea correcto
		pageDoc.setDrawColor(0, 0, 0)
		pageDoc.setLineWidth(0.5)
		
		const headerHalfWidth = 91
		const leftBoxX = 14
		const rightBoxX = 14 + headerHalfWidth
		
		// Cuadro de cotización (izquierda)
		pageDoc.rect(leftBoxX, 10, headerHalfWidth, 20)
		
		// Cuadro del logo (derecha)
		pageDoc.rect(rightBoxX, 10, headerHalfWidth, 20)
		
		// Texto "Cotización: [número]"
		pageDoc.setFontSize(14)
		pageDoc.setFont("helvetica", "bold")
		pageDoc.text(`Cotización: ${bidOrder.quotation}`, leftBoxX + headerHalfWidth / 2, 20, { align: "center" })
		
		// Logo
		try {
			let base64Logo = null
			const logoCenterX = rightBoxX + headerHalfWidth / 2
			if (bidOrder.company === 'SD') {
				base64Logo = await convertImageToBase64("/icons/logo_sdosPDF.png")
				const logoWidth = 35
				const logoHeight = 15
				const logoX = logoCenterX - logoWidth / 2
				const logoY = 10 + (20 - logoHeight) / 2
				pageDoc.addImage(base64Logo, 'PNG', logoX, logoY, logoWidth, logoHeight)
			} else {
				base64Logo = await convertImageToBase64("/icons/logo_arcasa.png")
				const logoWidth = 32
				const logoHeight = 15
				const logoX = logoCenterX - logoWidth / 2
				const logoY = 10 + (20 - logoHeight) / 2
				pageDoc.addImage(base64Logo, 'PNG', logoX, logoY, logoWidth, logoHeight)
			}
		} catch (error) {
			console.error("Error al convertir la imagen a base64:", error)
		}
		
		// Cuadro de descripción
		const descBoxX = 14
		const descBoxY = 30
		const descBoxWidth = 182
		pageDoc.setFontSize(10)
		pageDoc.setFont("helvetica", "normal")
		const descriptionText = ""
		const descriptionLines = pageDoc.splitTextToSize(descriptionText, 166)
		const descriptionLineHeight = 4.5
		const descriptionPaddingY = 2
		const totalTextHeight = descriptionLines.length * descriptionLineHeight
		const descBoxHeight = Math.max(10, totalTextHeight + descriptionPaddingY * 2)

		// Dibujar el cuadro con altura dinámica según el texto
		pageDoc.rect(descBoxX, descBoxY, descBoxWidth, descBoxHeight)

		// Posicionar el texto dentro del cuadro dejando un pequeño padding superior
		const startY = descBoxY + descriptionPaddingY + descriptionLineHeight
		descriptionLines.forEach((line, index) => {
			pageDoc.text(line, 105, startY + (index * descriptionLineHeight), { align: "center" })
		})
		
		// Cuadros de número de revisión y fecha vigencia
		const halfWidth = 91
		const revisionBoxTopY = descBoxY + descBoxHeight
		pageDoc.rect(14, revisionBoxTopY, halfWidth, 10)
		pageDoc.setFont("helvetica", "normal")
		pageDoc.setFontSize(10)
		pageDoc.text("Número de revisión:", 16, revisionBoxTopY + 7)
		pageDoc.rect(14 + halfWidth, revisionBoxTopY, halfWidth, 10)
		pageDoc.rect(14, revisionBoxTopY + 10, halfWidth, 10)
		pageDoc.text("Fecha vigencia:", 16, revisionBoxTopY + 17)
		pageDoc.rect(14 + halfWidth, revisionBoxTopY + 10, halfWidth, 10)
	}
	
	// Función auxiliar para verificar espacio y agregar página si es necesario
	const checkSpaceAndAddPage = async (currentY, spaceNeeded) => {
		const pageHeight = doc.internal.pageSize.height
		const footerHeight = 34
		const footerY = pageHeight - footerHeight - 10
		const minSpaceBeforeFooter = 10
		
		// Verificar y agregar páginas hasta que haya espacio suficiente
		let newY = currentY
		while (newY + spaceNeeded > footerY - minSpaceBeforeFooter) {
			// No dibujar footer aquí, se dibujará al final con el número correcto de páginas
			doc.addPage()
			await drawHeader(doc)
			newY = 70 // Posición Y inicial después del header
		}
		return newY
	}

	// Dibujar el header inicial
	await drawHeader(doc)

	doc.setFontSize(10)

	let y = 65
	const lineHeight = 7

	// Array de nombres de meses (reutilizable en toda la función)
	const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", 
						"Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]

	// Formatear fecha en español
	const dateNow = new Date()
	const day = dateNow.getDate()
	const month = dateNow.getMonth()
	const year = dateNow.getFullYear()
	const cityName = bidOrder.city || 'Santa Fe'
	const formattedDate = `${cityName}, ${day} de ${monthNames[month]} de ${year}`

	// Información del cliente y proyecto
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)

	// Espacio por encima de la fecha
	y += lineHeight

	// FECHA - alineada a la derecha (arriba del comitente)
	doc.setFont("helvetica", "normal")
	doc.text(formattedDate, 196, y, { align: "right" })

	// Espacio por debajo de la fecha
	y += lineHeight + lineHeight * 0.3

	// COMITENTE
	doc.setFont("helvetica", "normal")
	let clientsText = ''
	if (bidOrder.clientData && Array.isArray(bidOrder.clientData) && bidOrder.clientData.length > 0) {
		// Construir cadena de nombres desde clientData
		clientsText = bidOrder.clientData.map(client => client.name).filter(name => name).join(', ')
	} else if (bidOrder.clientNames) {
		// Fallback a clientNames si clientData no está disponible
		clientsText = bidOrder.clientNames.replace(/,\s*$/, '')
	} else {
		clientsText = '--'
	}
	doc.text(clientsText, 16, y)

	y += lineHeight + lineHeight * 0.5

	// INTERLOCUTOR (si existe en clientData, sino en bidOrder, sino dejamos espacio)
	doc.setFont("helvetica", "bold")
	doc.text("Atención:", 14, y)
	let interlocutor = '--'
	if (bidOrder.clientData && Array.isArray(bidOrder.clientData) && bidOrder.clientData.length > 0) {
		// Tomar el interlocutor del primer cliente que tenga uno
		const firstClientWithInterlocutor = bidOrder.clientData.find(client => client.interlocutor)
		if (firstClientWithInterlocutor) {
			interlocutor = firstClientWithInterlocutor.interlocutor
		}
	}
	doc.text(interlocutor, 40, y)

	y += lineHeight + 2

	// SU DESPACHO:
	doc.setFont("helvetica", "bold")
	doc.text("Su despacho:", 14, y)

	y += lineHeight + 2

	// REFERENCIAS - alineada a la derecha
	// Primera parte: "REFERENCIAS: [quotation]." en mayúsculas, bold y subrayado
	doc.setFont("helvetica", "bold")
	const quotationText = `REFERENCIAS: ${bidOrder.quotation}.`.toUpperCase()
	const quotationTextWidth = doc.getTextWidth(quotationText)
	const textX = 196 // Posición x para alineación derecha
	const marginLeft = 14
	
	// Dibujar la primera parte (con bold)
	doc.text(quotationText, textX, y, { align: "right" })
	
	// Dibujar línea de subrayado solo hasta el punto
	const underlineY = y + 1.5
	const underlineStartX = textX - quotationTextWidth
	doc.setLineWidth(0.5)
	doc.line(underlineStartX, underlineY, textX, underlineY)
	
	// Segunda parte: proyecto en letra normal, sin subrayar, siempre en la línea de abajo, alineado a la derecha
	if (bidOrder.proyect) {
		y += lineHeight
		doc.setFont("helvetica", "normal")
		doc.text(bidOrder.proyect, textX, y, { align: "right" })
	}

	y += lineHeight + 2

	// Texto introductorio
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	const introText = "Nos dirigimos a usted con el objeto de elevarle el Plan de trabajo y Cotización solicitado, para la elaboración de las siguientes tareas profesionales:"
	const introLines = doc.splitTextToSize(introText, 182)
	doc.text(introLines, 14, y)
	y += lineHeight

	// Tabla de ítems seleccionados
	const itemsArray = Array.isArray(selectedItems) ? selectedItems : []
	let totalAmount = 0
	
	// Función para formatear números con separadores de miles
	const formatNumber = (num) => {
		return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}
	
	const tableBody = []
	
	if (itemsArray.length > 0) {
		itemsArray.forEach((item) => {
			const itemName = item.name || ''
			const itemDescription = item.description || ''
			const days = item.days || item.plazo || item.deadline || ''
			const price = (item.price !== undefined && item.price !== null) ? Number(item.price) : 0
			totalAmount += price
			
			// Una sola fila por item: nombre, descripción, plazo, monto
			tableBody.push([
				itemName,
				itemDescription,
				days ? days.toString() : '',
				`$ ${formatNumber(price)}`
			])
		})
		
		// Agregar fila de total
		tableBody.push([
			'',
			'Total sin IVA',
			'',
			`$ ${formatNumber(totalAmount)}`
		])
	} else {
		tableBody.push(['Sin ítems seleccionados', '-', '-', '-'])
	}

	// Calcular el margen inferior basado en la posición del footer
	const pageHeight = doc.internal.pageSize.height
	const footerHeight = 34
	const footerY = pageHeight - footerHeight - 10
	// margin.bottom es la distancia desde el final de la página hasta donde queremos que termine la tabla
	// El footer empieza en footerY, así que margin.bottom = pageHeight - footerY + margen de seguridad
	// Reducimos el margen de seguridad para permitir que entre más texto en la primera página
	const marginBottom = pageHeight - footerY + 5 // 44 + 5 = 49 puntos desde el final de la página
	
	const finalY = doc.autoTable({
		startY: y + lineHeight,
		head: [['Nombre', 'Descripción', 'Plazo (dias)', 'Monto sin IVA']],
		body: tableBody,
		columnStyles: {
			0: { cellWidth: 45, cellMinWidth: 45 }, // Nombre - ancho fijo
			1: { cellWidth: 85, cellMinWidth: 85 }, // Descripción - ancho fijo
			2: { cellWidth: 22, halign: 'center' }, // Plazo (dias)
			3: { cellWidth: 30, halign: 'right' } // Monto sin IVA
		},
		headStyles: {
			fillColor: [255, 255, 255],
			textColor: [0, 0, 0],
			fontStyle: 'bold',
			lineWidth: 0.5
		},
		bodyStyles: {
			fillColor: [255, 255, 255], // Fondo blanco para todas las celdas del cuerpo
			textColor: [0, 0, 0],
			lineWidth: 0.5,
			lineColor: [0, 0, 0]
		},
		styles: {
			fillColor: [255, 255, 255], // Fondo blanco para todas las celdas
			lineWidth: 0.5,
			lineColor: [0, 0, 0]
		},
		alternateRowStyles: {
			fillColor: [255, 255, 255] // Desactivar colores alternos, mantener blanco
		},
		didParseCell: function(data) {
			// Forzar fondo blanco en todas las celdas
			data.cell.styles.fillColor = [255, 255, 255]
			
			// Aplicar negrita a la fila de total
			if (data.row.raw[1] === 'Total sin IVA') {
				data.cell.styles.fontStyle = 'bold'
			}
		},
		didDrawPage: function(data) {
			// Verificar si la tabla se superpuso con el footer y corregir las líneas
			const currentPageHeight = data.doc.internal.pageSize.height
			const currentFooterY = currentPageHeight - footerHeight - 10
			const tableEndY = data.cursor.y
			
			// Si la tabla terminó después del inicio del footer, borrar las líneas verticales que se superpusieron
			if (tableEndY > currentFooterY) {
				// Guardar el estado actual del color
				const currentDrawColor = data.doc.getDrawColor()
				
				// Dibujar líneas blancas sobre las líneas verticales de la tabla que se superpusieron
				data.doc.setDrawColor(255, 255, 255) // Blanco
				data.doc.setLineWidth(1.5) // Línea más gruesa para asegurar que cubra completamente
				
				// Calcular las posiciones X de las columnas (basado en los anchos de columna)
				const leftMargin = 14
				const col1X = leftMargin
				const col2X = leftMargin + 45
				const col3X = leftMargin + 45 + 85
				const col4X = leftMargin + 45 + 85 + 22
				const col5X = leftMargin + 45 + 85 + 22 + 30 // Línea derecha de la última columna
				
				// Dibujar líneas blancas desde el footer hasta el final de la página
				const eraseStartY = currentFooterY
				const eraseEndY = currentPageHeight
				
				// Líneas verticales entre columnas
				data.doc.line(col1X, eraseStartY, col1X, eraseEndY) // Línea izquierda
				data.doc.line(col2X, eraseStartY, col2X, eraseEndY) // Entre col1 y col2
				data.doc.line(col3X, eraseStartY, col3X, eraseEndY) // Entre col2 y col3
				data.doc.line(col4X, eraseStartY, col4X, eraseEndY) // Entre col3 y col4
				data.doc.line(col5X, eraseStartY, col5X, eraseEndY) // Línea derecha
				
				// Restaurar el estado (getDrawColor puede devolver array o objeto)
				if (Array.isArray(currentDrawColor)) {
					data.doc.setDrawColor(currentDrawColor[0], currentDrawColor[1], currentDrawColor[2])
				} else {
					data.doc.setDrawColor(currentDrawColor.r || 0, currentDrawColor.g || 0, currentDrawColor.b || 0)
				}
				data.doc.setLineWidth(0.5) // Restaurar al ancho de línea estándar usado en el código
			}
		},
		margin: { left: 14, right: 14, bottom: marginBottom },
		pageBreak: 'auto',
		showHead: 'everyPage'
	})
	
	// Calcular espacio necesario para todas las secciones restantes
	const aclaracionText = "Aclaración: se consideran los plazos de elaboración propia, no se contemplan plazos de análisis de entidades reguladoras."
	const aclaracionLines = doc.splitTextToSize(aclaracionText, 182)
	const ofertaText = ` Se cotiza para la ejecución de los trabajos incluidos la suma de $${formatNumber(totalAmount)} + IVA.`
	const ofertaTextLines = doc.splitTextToSize(ofertaText, 182 - doc.getTextWidth("OFERTA:"))
	const paymentFormText = bidOrder.paymentForm || ''
	const paymentFormLines = doc.splitTextToSize(paymentFormText, 182 - doc.getTextWidth("FORMA DE PAGO:"))
	const redeterminacionText = `Monto ajustable según la variación del índice general del costo de la construcción dictado por la Cámara Argentina de la Construcción.`
	const redeterminacionAllLines = doc.splitTextToSize(redeterminacionText, 182 - doc.getTextWidth("REDETERMINACIÓN:"))
	
	// Primero verificar solo si la Aclaración cabe debajo de la tabla
	let currentY = finalY.lastAutoTable.finalY + 5
	const aclaracionSpaceNeeded = (aclaracionLines.length * lineHeight) + 5
	currentY = await checkSpaceAndAddPage(currentY, aclaracionSpaceNeeded)
	
	// Agregar texto de aclaración debajo de la tabla (siempre justo después de la tabla)
	const aclaracionY = currentY
	doc.setFont("helvetica", "bold")
	doc.setFontSize(10)
	doc.text(aclaracionLines, 14, aclaracionY)
	
	// Calcular posición Y después de la aclaración
	const ofertaY = aclaracionY + (aclaracionLines.length * lineHeight) + 5
	
	// Ahora verificar el espacio para las demás secciones (OFERTA, FORMA DE PAGO, etc.)
	const remainingSpaceNeeded = (ofertaTextLines.length * lineHeight) + 5 + // OFERTA
								(paymentFormLines.length * lineHeight) + 5 + // FORMA DE PAGO
								(1 * lineHeight) + 5 + // MES BASE
								(redeterminacionAllLines.length * lineHeight) + 10 // REDETERMINACIÓN + margen
	
	// Verificar espacio y agregar página si es necesario para las secciones restantes
	let nextY = ofertaY
	const previousY = nextY
	nextY = await checkSpaceAndAddPage(nextY, remainingSpaceNeeded)
	
	// Si se agregó una nueva página (nextY cambió a 70), centrar verticalmente las secciones
	let isCenteredOnNewPage = false
	if (nextY === 70 && previousY !== 70) {
		const pageHeight = doc.internal.pageSize.height
		const footerHeight = 34
		const footerY = pageHeight - footerHeight - 10
		const headerEndY = 70 // Posición después del header
		const availableSpace = footerY - headerEndY - 10 // Espacio disponible menos margen
		const totalContentHeight = remainingSpaceNeeded
		
		// Calcular la posición Y inicial para centrar verticalmente
		if (totalContentHeight < availableSpace) {
			nextY = headerEndY + (availableSpace - totalContentHeight) / 2
			isCenteredOnNewPage = true
		}
	}
	
	// Espaciado entre secciones: 10 puntos si está centrado en nueva página, 5 puntos si está en la misma página
	const sectionSpacing = isCenteredOnNewPage ? 15 : 5
	
	// Agregar texto OFERTA: en negrita y subrayado
	doc.setFont("helvetica", "bold")
	doc.setFontSize(12)
	const ofertaLabel = "OFERTA:"
	const ofertaLabelWidth = doc.getTextWidth(ofertaLabel)
	doc.text(ofertaLabel, 14, nextY)
	
	// Dibujar línea de subrayado para OFERTA: en color negro
	const ofertaUnderlineY = nextY + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0) // Color negro
	doc.line(14, ofertaUnderlineY, 14 + ofertaLabelWidth, ofertaUnderlineY)
	
	// Agregar el resto del texto en letra normal
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	const ofertaTextX = 14 + ofertaLabelWidth
	doc.text(ofertaTextLines, ofertaTextX, nextY)
	
	// Calcular posición Y después del texto de OFERTA
	nextY = nextY + (ofertaTextLines.length * lineHeight) + sectionSpacing
	
	// Agregar texto FORMA DE PAGO: en negrita y subrayado
	doc.setFont("helvetica", "bold")
	doc.setFontSize(10)
	const formaPagoLabel = "FORMA DE PAGO:"
	doc.setFontSize(12)
	const formaPagoLabelWidth = doc.getTextWidth(formaPagoLabel)
	doc.text(formaPagoLabel, 14, nextY)
	
	// Dibujar línea de subrayado para FORMA DE PAGO: en color negro
	const formaPagoUnderlineY = nextY + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0) // Color negro
	doc.line(14, formaPagoUnderlineY, 14 + formaPagoLabelWidth, formaPagoUnderlineY)
	
	// Agregar el valor de paymentForm en letra normal
	doc.setFont("helvetica", "normal")
	const paymentFormX = 14 + formaPagoLabelWidth + 2
	doc.text(paymentFormLines, paymentFormX, nextY)
	
	// Calcular posición Y después del texto de FORMA DE PAGO
	nextY = nextY + (paymentFormLines.length * lineHeight) + sectionSpacing
	
	// Calcular el mes anterior a la fecha de cotización
	let mesAnterior = ''
	let añoAnterior = ''
	if (bidOrder.startDate) {
		try {
			// Parsear la fecha en formato DD/MM/YYYY
			const dateParts = bidOrder.startDate.split('/').map(Number)
			if (dateParts.length === 3) {
				const day = dateParts[0]
				const month = dateParts[1]
				const year = dateParts[2]
				
				// Calcular el mes anterior
				let previousMonth = month - 1
				let previousYear = year
				
				if (previousMonth === 0) {
					previousMonth = 12
					previousYear = year - 1
				}
				
				mesAnterior = monthNames[previousMonth - 1]
				añoAnterior = previousYear.toString()
			}
		} catch (error) {
			console.error('Error al calcular mes anterior:', error)
		}
	}
	
	// Agregar texto MES BASE: en negrita y subrayado
	doc.setFont("helvetica", "bold")
	doc.setFontSize(10)
	const mesBaseLabel = "MES BASE:"
	doc.setFontSize(12)
	const mesBaseLabelWidth = doc.getTextWidth(mesBaseLabel)
	doc.text(mesBaseLabel, 14, nextY)
	
	// Dibujar línea de subrayado para MES BASE: en color negro
	const mesBaseUnderlineY = nextY + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0) // Color negro
	doc.line(14, mesBaseUnderlineY, 14 + mesBaseLabelWidth, mesBaseUnderlineY)
	
	// Agregar el texto con el mes y año anterior en letra normal
	doc.setFont("helvetica", "normal")
	const mesBaseText = mesAnterior && añoAnterior ? `${mesAnterior} - ${añoAnterior}` : (mesAnterior ? mesAnterior : '-')
	const mesBaseTextX = 14 + mesBaseLabelWidth + 2
	const mesBaseTextLines = doc.splitTextToSize(mesBaseText, 182 - mesBaseLabelWidth)
	doc.text(mesBaseTextLines, mesBaseTextX, nextY)
	
	// Calcular posición Y después del texto de MES BASE
	nextY = nextY + (mesBaseTextLines.length * lineHeight) + sectionSpacing
	
	// Agregar texto REDETERMINACIÓN: en negrita y subrayado
	doc.setFont("helvetica", "bold")
	doc.setFontSize(12)
	const redeterminacionLabel = "REDETERMINACIÓN:"
	const redeterminacionLabelWidth = doc.getTextWidth(redeterminacionLabel)
	doc.text(redeterminacionLabel, 14, nextY)
	
	// Dibujar línea de subrayado para REDETERMINACIÓN: en color negro
	const redeterminacionUnderlineY = nextY + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0) // Color negro
	doc.line(14, redeterminacionUnderlineY, 14 + redeterminacionLabelWidth, redeterminacionUnderlineY)
	
	// Agregar el resto del texto en letra normal
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	const redeterminacionTextX = 14 + redeterminacionLabelWidth + 2
	const redeterminacionFirstLine = redeterminacionAllLines[0] || ""
	const redeterminacionRemainingText = redeterminacionAllLines.slice(1).join(" ")

	// Primera línea a la derecha del título
	doc.text(redeterminacionFirstLine, redeterminacionTextX, nextY)

	// Resto de líneas empezando en x = 14
	let redeterminacionLinesCount = 1
	if (redeterminacionRemainingText) {
		const remainingRedetLines = doc.splitTextToSize(redeterminacionRemainingText, 182)
		const redetNextY = nextY + lineHeight
		doc.text(remainingRedetLines, 14, redetNextY)
		redeterminacionLinesCount += remainingRedetLines.length
	}
	
	// No dibujar footer aquí, se dibujará al final con el número correcto de páginas

	// ---------- PÁGINA 2 ----------
	doc.addPage()
	
	// Asegurar que el color de dibujo sea negro y el ancho de línea correcto
	doc.setDrawColor(0, 0, 0)
	doc.setLineWidth(0.5)

	// Reutilizar el mismo encabezado de la página 1
	// Ancho total disponible: 196 - 14 = 182 puntos
	// Cada cuadro ocupa la mitad: 182 / 2 = 91 puntos
	const headerHalfWidthPage2 = 91
	const leftBoxXPage2 = 14
	const rightBoxXPage2 = 14 + headerHalfWidthPage2 // 105
	
	// Cuadro de cotización (izquierda) - página 2 - mitad del ancho
	doc.rect(leftBoxXPage2, 10, headerHalfWidthPage2, 20)
	
	// Cuadro del logo (derecha) - página 2 - mitad del ancho
	doc.rect(rightBoxXPage2, 10, headerHalfWidthPage2, 20)
	
	// Texto "Cotización: [número]" centrado - página 2
	doc.setFont("helvetica", "bold")
	doc.setFontSize(14)
	doc.text(`Cotización: ${bidOrder.quotation}`, leftBoxXPage2 + headerHalfWidthPage2 / 2, 20, { align: "center" })
	
	// Logo centrado en el cuadro derecho - página 2
	try {
		let base64LogoPage2 = null
		const logoCenterXPage2 = rightBoxXPage2 + headerHalfWidthPage2 / 2 // Centro del cuadro derecho: 105 + 91/2 = 150.5
		if (bidOrder.company === 'SD') {
			base64LogoPage2 = await convertImageToBase64("/icons/logo_sdosPDF.png")
			const logoWidthPage2 = 35
			const logoHeightPage2 = 15
			const logoXPage2 = logoCenterXPage2 - logoWidthPage2 / 2 // Centrar el logo: 150.5 - 35/2 = 133
			const logoYPage2 = 10 + (20 - logoHeightPage2) / 2 // Centrar verticalmente: 10 + (20-15)/2 = 12.5
			doc.addImage(base64LogoPage2, 'PNG', logoXPage2, logoYPage2, logoWidthPage2, logoHeightPage2)
		} else {
			base64LogoPage2 = await convertImageToBase64("/icons/logo_arcasa.png")
			const logoWidthPage2 = 32
			const logoHeightPage2 = 15
			const logoXPage2 = logoCenterXPage2 - logoWidthPage2 / 2 // Centrar el logo: 150.5 - 32/2 = 134.5
			const logoYPage2 = 10 + (20 - logoHeightPage2) / 2 // Centrar verticalmente: 10 + (20-15)/2 = 12.5
			doc.addImage(base64LogoPage2, 'PNG', logoXPage2, logoYPage2, logoWidthPage2, logoHeightPage2)
		}
	} catch (error) {
		console.error("Error al convertir la imagen a base64 (cotización aprobada - página 2):", error)
	}

	// Cuadro completo para "Descripción de la cotización" que ocupa todo el ancho - página 2
	const descBoxXPage2 = 14
	const descBoxYPage2 = 30
	const descBoxWidthPage2 = 182
	doc.setFontSize(10)
	doc.setFont("helvetica", "normal")
	const descriptionTextPage2 = ""
	// Dividir el texto en líneas que quepan dentro del ancho del cuadro (con margen de 8 puntos a cada lado)
	const availableWidthPage2 = descBoxWidthPage2 - 16 // 8 puntos de margen a cada lado
	const descriptionLinesPage2 = doc.splitTextToSize(descriptionTextPage2, availableWidthPage2)
	// Calcular altura dinámica del cuadro según el texto
	const lineHeightPage2Desc = 4.5
	const descriptionPaddingYPage2 = 2
	const totalTextHeightPage2 = descriptionLinesPage2.length * lineHeightPage2Desc
	const descBoxHeightPage2 = Math.max(10, totalTextHeightPage2 + descriptionPaddingYPage2 * 2)

	// Dibujar cuadro con altura dinámica
	doc.rect(descBoxXPage2, descBoxYPage2, descBoxWidthPage2, descBoxHeightPage2)

	// Calcular la posición Y inicial para dibujar el texto dentro del cuadro
	const startYPage2 = descBoxYPage2 + descriptionPaddingYPage2 + lineHeightPage2Desc
	// Dibujar cada línea centrada horizontalmente
	descriptionLinesPage2.forEach((line, index) => {
		doc.text(line, 105, startYPage2 + (index * lineHeightPage2Desc), { align: "center" })
	})

	// Ancho de cada mitad (182 puntos totales / 2 = 91 puntos cada uno) - página 2
	// Reutilizar headerHalfWidthPage2 que ya está declarado arriba

	// Cuadro de Número de Revisión (mitad izquierda) - página 2
	const revisionBoxTopYPage2 = descBoxYPage2 + descBoxHeightPage2
	doc.rect(14, revisionBoxTopYPage2, headerHalfWidthPage2, 10)
	doc.setFont("helvetica", "normal")
	doc.setFontSize(10)
	doc.text("Número de revisión:", 16, revisionBoxTopYPage2 + 7)
	
	// Cuadro vacío para Número de Revisión (mitad derecha) - página 2
	doc.rect(14 + headerHalfWidthPage2, revisionBoxTopYPage2, headerHalfWidthPage2, 10)
	
	// Cuadro de Fecha vigencia (mitad izquierda) - página 2
	doc.rect(14, revisionBoxTopYPage2 + 10, headerHalfWidthPage2, 10)
	doc.text("Fecha vigencia:", 16, revisionBoxTopYPage2 + 17)
	
	// Cuadro vacío para Fecha vigencia (mitad derecha) - página 2
	doc.rect(14 + headerHalfWidthPage2, revisionBoxTopYPage2 + 10, headerHalfWidthPage2, 10)

	// ----- Contenido de página 2: COSTOS INCLUIDOS -----
	const revisionBoxesBottomYPage2 = revisionBoxTopYPage2 + 20
	// Iniciar el bloque de COSTOS INCLUIDOS casi pegado a los cuadros anteriores,
	// para no agregar espacio en blanco extra que empuje la firma hacia abajo
	let yPage2 = revisionBoxesBottomYPage2 + 8
	const lineHeightPage2 = 7

	// Título COSTOS INCLUIDOS: en bold y subrayado
	doc.setFont("helvetica", "bold")
	doc.setFontSize(12)
	const costosIncluidosLabel = "COSTOS INCLUIDOS:"
	const costosIncluidosLabelWidth = doc.getTextWidth(costosIncluidosLabel)
	doc.text(costosIncluidosLabel, 14, yPage2)

	// Subrayado para COSTOS INCLUIDOS:
	const costosIncluidosUnderlineY = yPage2 + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0)
	doc.line(14, costosIncluidosUnderlineY, 14 + costosIncluidosLabelWidth, costosIncluidosUnderlineY)

	// Texto introductorio, primera línea a la derecha del título y el resto desde x = 14
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	const costosIntroText = "Los siguientes costos que se considerarán incluidos en esta presente cotización, a saber:"
	const introStartX = 14 + costosIncluidosLabelWidth + 2 // un pequeño espacio después del título
	const introAvailableWidth = 196 - introStartX // desde introStartX hasta el margen derecho
	const costosIntroAllLines = doc.splitTextToSize(costosIntroText, introAvailableWidth)
	const costosIntroFirstLine = costosIntroAllLines[0] || ""
	const costosIntroRemaining = costosIntroAllLines.slice(1).join(" ")

	// Primera línea, a la derecha del título
	doc.text(costosIntroFirstLine, introStartX, yPage2)

	// Resto del texto, empezando en x = 14
	let introLinesCount = 1
	if (costosIntroRemaining) {
		const remainingIntroLines = doc.splitTextToSize(costosIntroRemaining, 182)
		yPage2 += lineHeightPage2
		doc.text(remainingIntroLines, 14, yPage2)
		introLinesCount += remainingIntroLines.length
	}

	// Avanzar después del texto introductorio (con menos espacio antes de la lista)
	yPage2 += (introLinesCount - 1) * lineHeightPage2 + (lineHeightPage2 / 2)

	// Lista de costos incluidos (viñetas)
	const costosItems = [
		"Costos de mantenimiento de matrículas de todos los profesionales intervinientes.",
		"Costos de mantenimiento de cuotas de jubilación de todos los profesionales intervinientes, estando a cargo del comitente el pago de aportes colegiales y jubilatorios de ley. No incluye ningún tipo de análisis, estudios, ensayos de agua, suelo y/o aire.",
		"Costos de seguros de accidentes personales de profesionales intervinientes.",
		"Costos de transporte y estadía a esa localidad de todos los viajes que fueran necesarios.",
		"Costos de impresión, ploteo y armado de dos juegos."
	]

	costosItems.forEach(text => {
		// Viñeta
		doc.text("•", 18, yPage2)

		// Texto del ítem, con sangría
		const itemLines = doc.splitTextToSize(text, 182 - 10) // 10 pts menos para la sangría
		doc.text(itemLines, 24, yPage2)

		yPage2 += itemLines.length * lineHeightPage2 + 2
	})

	// Espacio antes del bloque de COSTOS Y TAREAS NO INCLUIDAS
	yPage2 += lineHeightPage2

	// COSTOS Y TAREAS NO INCLUIDAS: en bold y subrayado, con texto a la derecha
	doc.setFont("helvetica", "bold")
	doc.setFontSize(12)
	const costosNoIncluidosLabel = "COSTOS Y TAREAS NO INCLUIDAS:"
	const costosNoIncluidosLabelWidth = doc.getTextWidth(costosNoIncluidosLabel)
	doc.text(costosNoIncluidosLabel, 14, yPage2)

	// Subrayado solo bajo el título
	const costosNoIncluidosUnderlineY = yPage2 + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0)
	doc.line(14, costosNoIncluidosUnderlineY, 14 + costosNoIncluidosLabelWidth, costosNoIncluidosUnderlineY)

	// Texto corrido: primera línea a la derecha del título, resto desde el margen izquierdo
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	const costosNoIncluidosText = "No se incluyen aportes colegiales ni de caja de ingeniería, estudio ni ensayos de suelos, evaluación económica, análisis fisicoquímicos de aguas, perforaciones, ensayos de bombeo, verificaciones estructurales y todo tipo de análisis, estudios y/o ensayos de cualquier material que no esté especificado en la presente cotización. Tampoco gestiones ni aprobaciones en entes no especificados en la presente propuesta."
	const costosNoIncluidosStartX = 14 + costosNoIncluidosLabelWidth + 2
	const costosNoIncluidosAvailableWidth = 196 - costosNoIncluidosStartX

	// Partir el texto: primera línea junto al título, resto alineado al margen izquierdo
	const costosNoIncluidosFirstLines = doc.splitTextToSize(costosNoIncluidosText, costosNoIncluidosAvailableWidth)
	const firstLineNoIncluidos = costosNoIncluidosFirstLines[0] || ""
	const remainingNoIncluidosText = costosNoIncluidosFirstLines.slice(1).join(" ")

	// Primera línea, a la derecha del título
	doc.text(firstLineNoIncluidos, costosNoIncluidosStartX, yPage2)

	// Líneas restantes, desde el margen izquierdo (x = 14)
	if (remainingNoIncluidosText) {
		const remainingLines = doc.splitTextToSize(remainingNoIncluidosText, 182)
		yPage2 += lineHeightPage2
		doc.text(remainingLines, 14, yPage2)
		// Menos espacio extra después del bloque
		yPage2 += remainingLines.length * lineHeightPage2 * 0.7
	}

	// Espacio antes del bloque de PLAZO DE MANTENIMIENTO DE OFERTA (ligeramente menor)
	yPage2 += lineHeightPage2 * 0.7

	// PLAZO DE MANTENIMIENTO DE OFERTA: en bold y subrayado, con texto a la derecha
	doc.setFont("helvetica", "bold")
	doc.setFontSize(12)
	const plazoOfertaLabel = "PLAZO DE MANTENIMIENTO DE OFERTA:"
	const plazoOfertaLabelWidth = doc.getTextWidth(plazoOfertaLabel)
	doc.text(plazoOfertaLabel, 14, yPage2)

	// Subrayado solo bajo el título
	const plazoOfertaUnderlineY = yPage2 + 1.5
	doc.setLineWidth(0.5)
	doc.setDrawColor(0, 0, 0)
	doc.line(14, plazoOfertaUnderlineY, 14 + plazoOfertaLabelWidth, plazoOfertaUnderlineY)

	// Texto corrido: primera línea a la derecha del título, resto desde el margen izquierdo
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	const plazoOfertaText = "El mantenimiento de esta oferta es de 5 días corridos a partir de la fecha de recepción de la presente."
	const plazoOfertaStartX = 14 + plazoOfertaLabelWidth + 2
	const plazoOfertaAvailableWidth = 196 - plazoOfertaStartX

	const plazoOfertaFirstLines = doc.splitTextToSize(plazoOfertaText, plazoOfertaAvailableWidth)
	const plazoOfertaFirstLine = plazoOfertaFirstLines[0] || ""
	const plazoOfertaRemainingText = plazoOfertaFirstLines.slice(1).join(" ")

	// Primera línea, a la derecha del título
	doc.text(plazoOfertaFirstLine, plazoOfertaStartX, yPage2)

	// Líneas restantes, desde el margen izquierdo
	if (plazoOfertaRemainingText) {
		const plazoRemainingLines = doc.splitTextToSize(plazoOfertaRemainingText, 182)
		yPage2 += lineHeightPage2
		doc.text(plazoRemainingLines, 14, yPage2)
	}

	// Saludo final
	yPage2 += lineHeightPage2 * 2
	doc.setFont("helvetica", "normal")
	doc.setFontSize(12)
	doc.text("Saludos cordiales.", 14, yPage2)

	// Firma alineada a la derecha, en la línea siguiente al saludo
	// Seleccionar la firma basándose en el email del usuario
	let firmaFileName = "firmaGabriel.png" // Por defecto
	if (userEmail === "ingenierosaravia@sdingenieria.com.ar") {
		firmaFileName = "firmaRuben.png"
	} else if (userEmail === "melinabodini@sdingenieria.com.ar") {
		firmaFileName = "firmaMelina.png"
	}
	
	try {
		const firmaBase64 = await convertImageToBase64(`/icons/${firmaFileName}`)
		const firmaWidth = 65
		const firmaHeight = 35
		const firmaX = 196 - firmaWidth // alineado al margen derecho
		const firmaY = yPage2 + lineHeightPage2 * 0.7 // debajo del saludo
		doc.addImage(firmaBase64, 'PNG', firmaX, firmaY, firmaWidth, firmaHeight)
	} catch (error) {
		console.error(`Error al cargar la firma (${firmaFileName}):`, error)
	}

	// ----- Dibujar footers en todas las páginas con el número correcto -----
	// Obtener el número total de páginas después de crear todo el documento
	const totalPages = doc.internal.getNumberOfPages()
	
	// Iterar sobre cada página y dibujar el footer con el número correcto
		for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
		doc.setPage(pageNum)
		const pageHeight = doc.internal.pageSize.height
		const footerHeight = 34
		const footerY = pageHeight - footerHeight - 10
		
		// Crear cuadro del pie de página
		doc.setLineWidth(0.5)
		doc.setDrawColor(0, 0, 0)
		doc.rect(14, footerY, 182, footerHeight)
		
		// Línea vertical para dividir logo (izquierda) e información de contacto (derecha)
		const verticalLineX = 14 + 91
		doc.line(verticalLineX, footerY, verticalLineX, footerY + 22)
		
		// Líneas horizontales para dividir las 3 filas de contacto dentro del cuadro derecho
		const contactRow1Y = footerY + 10
		const contactRow2Y = footerY + 16
		doc.line(verticalLineX, contactRow1Y, 196, contactRow1Y)
		doc.line(verticalLineX, contactRow2Y, 196, contactRow2Y)
		
		// Línea horizontal para dividir sección superior (logo + contacto) e inferior (dirección + web)
		const horizontalLineY = footerY + 22
		doc.line(14, horizontalLineY, 196, horizontalLineY)
		
		// Cargar y agregar logo GRUPO SADE a la izquierda
		try {
			const base64LogoSade = await convertImageToBase64("/icons/logo-grupoSadeColor.png")
			const logoWidth = 50
			const logoHeight = 18
			const leftBoxWidth = verticalLineX - 14
			const logoX = 14 + (leftBoxWidth - logoWidth) / 2
			const logoY = footerY + (20 - logoHeight) / 2 + 2
			doc.addImage(base64LogoSade, 'PNG', logoX, logoY, logoWidth, logoHeight)
		} catch (error) {
			console.error("Error al cargar logo GRUPO SADE:", error)
			doc.setFont("helvetica", "bold")
			doc.setFontSize(12)
			doc.text("GRUPO SADE", 16, footerY + 13)
		}
		
		// Información de contacto a la derecha (arriba) - 3 filas horizontales
		doc.setFont("helvetica", "normal")
		doc.setFontSize(10)
		const contactCenterX = verticalLineX + (196 - verticalLineX) / 2
		
		// Usar el número total de páginas correcto
		doc.text(`Página ${pageNum} de ${totalPages}`, contactCenterX, footerY + 6, { align: "center" })
		doc.text("Email: gabrielsaravia@sdingenieria.com.ar", contactCenterX, footerY + 14, { align: "center" })
		doc.text("Cel.: +54 342 407-4511", contactCenterX, footerY + 20, { align: "center" })
		
		// Dirección en el medio
		doc.setFontSize(9)
		doc.text("Pedro Vittori 4173 | Santa Fe, Argentina", 105, footerY + 26, { align: "center" })
		
		// Línea horizontal entre dirección y website
		const footerMiddleLineY = footerY + 29
		doc.line(14, footerMiddleLineY, 196, footerMiddleLineY)
		
		// Website en la parte inferior
		doc.setFont("helvetica", "bold")
		doc.setFontSize(9)
		doc.text("www.gruposade.com.ar", 105, footerY + 32, { align: "center" })
	}

	return doc
}

